import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Observable, map, of, tap} from 'rxjs';
import { LocalStorageItem } from '../enums/local-storage-item';
import { BackendAPI } from '../models/backend-api';
import { GetMessagesResponse, Message, PostMessageRequest, PostMessageResponse } from '../models/message.model';
import { UserProfilesService } from './user-profiles.service';
import {UserProfile} from "@core/models/user-profile";
import {catchError} from "rxjs/operators";

@Injectable({
	providedIn: 'root'
})
export class MessageService {

	private alreadyReadMessageIds: Array<String> = [];

	constructor (
		private httpClient: HttpClient,
	) {
		if (localStorage.getItem(LocalStorageItem.ALREADY_READ_MESSAGES)) {
			const data = localStorage.getItem(LocalStorageItem.ALREADY_READ_MESSAGES);
			if (data) {
				this.alreadyReadMessageIds = JSON.parse(data);
			}
		}
	}

	/**
	 * Fetches a single page of conversation heads from the API.
	 * @param unread Whether to filter for unread messages
	 * @param pageNo Page number to fetch
	 * @param pageSize Number of messages per page
	 * @param userProfile Current user profile for marking read status
	 * @returns Observable of messages
	 */
	getConversationHeads(unread: boolean, pageNo: number, pageSize: number,
						 userProfile: UserProfile): Observable<Message[]> {
		const params = new HttpParams()
			.set('messageType', MessageTypes.CHAT)
			.set('pageNo', pageNo.toString())
			.set('pageSize', pageSize.toString())
			.set('unread', unread.toString());

		return this.httpClient
			.get<GetMessagesResponse>(BackendAPI.getMessages, { params })
			.pipe(
				map(res => {
						return res.messages
							.map(x => {
								x.isRead = x.senderUserID === userProfile.id || this.isMessageAlreadyRead(x.id);
								return x;
							});
					}
				),
				catchError((error) => {
					console.error(`API error for page ${pageNo}:`, error);
					return of([]);
				}),
			);
	}

	// Returns all chat messages for an order grouped by publisher
	getConversationsForOrderId(orderId: string): Observable<Map<string, Message[]>> {
		let queryParams = new HttpParams();
		queryParams = queryParams.append("messageType", MessageTypes.CHAT);
		queryParams = queryParams.append("pageNo", 1);
		queryParams = queryParams.append("pageSize", 999); //TODO
		queryParams = queryParams.append("orderId", orderId);
		return this.httpClient.get<GetMessagesResponse>(BackendAPI.getMessages, { params: queryParams })
			.pipe(
				map(res => {
					let sortedMessages = res.messages.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
					return sortedMessages.reduce(
						(map, message) => {
							const publisherId = message.toCompanyId ?? message.senderCompanyId ?? '';
							return map.set(publisherId, [...(map.get(publisherId) || []), message]);
						},
						new Map<string, Message[]>()
					);
				})
			);
	}

	sendMessage(orderId: string, publisherId: string, content: string): Observable<PostMessageResponse> {
		const message: PostMessageRequest = {
			action: "new",
			orderId: orderId,
			toCompanyId: publisherId,
			contentType: "text/plain;charset=UTF8",
			content: content
		}
		return this.httpClient.post<PostMessageResponse>(BackendAPI.postMessage, message);
	}

	/**
	 * Get notifications (up to a maximum that is currently 50)
	 * @returns Observable of notification messages.
	 */
	getAllNotifications(pageNo: number, pageSize: number): Observable<Message[]> {
		let queryParams = new HttpParams();
		queryParams = queryParams.append("messageType", MessageTypes.NOTIFICATION);
		queryParams = queryParams.append("pageNo", pageNo);
		queryParams = queryParams.append("pageSize", pageSize);
		return this.httpClient.get<GetMessagesResponse>(BackendAPI.getMessages, { params: queryParams })
			.pipe(
				map(res => {
						return res.messages
							.map(x => {
								x.isRead = this.isMessageAlreadyRead(x.id);
								return x;
							});
					}
				)
			);
	}

	/**
	 * Get recent notifications - i.e. those which have occurred since previous login
	 * @returns Observable of notification messages.
	 */
	getNotificationsSinceLastLogin(pageNo: number, pageSize = 50): Observable<Message[]> {
		// TODO Load in batches (onto the stream)
		let queryParams = new HttpParams();
		queryParams = queryParams.append("messageType", MessageTypes.NOTIFICATION);
		queryParams = queryParams.append("pageNo", pageNo);
		queryParams = queryParams.append("pageSize", pageSize);
		// queryParams = queryParams.append("unread", unread); //TODO This is currently inconsequential - they are all unread because they never get 'read'
		return this.httpClient.get<GetMessagesResponse>(BackendAPI.getMessages, { params: queryParams })
			.pipe(
				map(res => {
					// Only show notifications that occurred since the previous login
					// Mark as read according to local storage (TODO set DB isRead flag)
					let notifications = res.messages;
					const lastLoginTime = sessionStorage.getItem(LocalStorageItem.LAST_LOGIN);
					if (lastLoginTime) {
						notifications = notifications
							.filter(x => x.timestamp > lastLoginTime)
							.map(x => {
								x.isRead = this.isMessageAlreadyRead(x.id);
								return x;
							});
					}
					return notifications;
				})
			);
	}

	public markMessageAsRead(id: string) {
		if (!this.isMessageAlreadyRead(id)) {
			this.alreadyReadMessageIds.push(id);
		}
		localStorage.setItem(LocalStorageItem.ALREADY_READ_MESSAGES, JSON.stringify(this.alreadyReadMessageIds));
	}

	public isMessageAlreadyRead(id: string) {
		return this.alreadyReadMessageIds.some(x => x === id)
	}

	/**
	 * Processes messages by filtering, marking read status, grouping, and sorting.
	 * @param messages Array of messages to process
	 * @param user Current user profile
	 * @param unread Whether to filter for unread messages
	 * @returns Processed array of latest messages per conversation
	 */
	processMessages(messages: Message[], user: UserProfile, unread: boolean,): Message[] {
		if (!messages?.length) {
			return [];
		}

		let filteredMessages = messages;
		if (unread) {
			const lastLogin = sessionStorage.getItem(LocalStorageItem.LAST_LOGIN) ?? '';
			filteredMessages = messages.filter(
				(msg) => msg.timestamp > lastLogin,
			);
		}

		// Mark as read if sent by user or already read
		const markedMessages = filteredMessages.map((msg) => ({
			...msg,
			isRead: msg.senderUserID === user.id || this.isMessageAlreadyRead(msg.id),
		}));

		// Group by conversation and get latest message per group
		const grouped = this.groupByConversation(markedMessages);
		let latestMessages = this.getLatestPerGroup(grouped);

		// Filter out threads where user was the last sender if unread is true
		if (unread) {
			latestMessages = latestMessages.filter((msg) => msg.senderUserID !== user.id);
		}

		// Sort by timestamp
		// TODO: limit to 5 messages if unread is true.
		const result = latestMessages
			.sort((a, b) => b.timestamp.localeCompare(a.timestamp))

		return result;
	}

	/**
	 * Groups messages by orderId and companyId (sender or recipient).
	 * @param messages Array of messages to group
	 * @returns Map of conversation key to message array
	 */
	private groupByConversation(messages: Message[]): Map<string, Message[]> {
		return messages.reduce((map, msg) => {
			const key = `${msg.orderId}__${msg.senderCompanyId ?? msg.toCompanyId ?? ''}`;
			const group = map.get(key) || [];
			map.set(key, [...group, msg]);
			return map;
		}, new Map<string, Message[]>());
	}

	/**
	 * Gets the latest message from each conversation group.
	 * @param groups Map of conversation key to message array
	 * @returns Array of latest messages per group
	 */
	private getLatestPerGroup(groups: Map<string, Message[]>): Message[] {
		const latest: Message[] = [];
		for (const group of groups.values()) {
			const latestInGroup = group.sort((a, b) =>
				b.timestamp.localeCompare(a.timestamp),
			)[0];
			if (latestInGroup) {
				latest.push(latestInGroup);
			}
		}
		return latest;
	}
}

export enum MessageTypes {
	NOTIFICATION = 0,
	CHAT = 1
}

export enum NotificationTypes {
	MODIFIED = 'MODIFIED',
	APPROVED = 'APPROVED',
	REJECTED = 'REJECTED',
	CONFIRM_DEADLINE_WARNING = 'CONFIRM DEADLINE WARNING',
	CONFIRM_DEADLINE_PASSED = 'CONFIRM DEADLINE PASSED'
}
